/**
 * Sign-up pop-up (snippets/pl-signup.liquid). Opens once a visitor has read a little (scrolled past the set share of
 * the page, or about to leave on desktop), never in the first 8 seconds, never on the cart or the custom builder, never
 * over another open layer (drawer, cookie banner), at most once per visit. "No thanks" or closing snoozes it for N days;
 * signing up stops it for good. The address goes to Klaviyo's client subscriptions API (public site ID, list id); a
 * failure shows a plain message and the visitor can try again.
 */
(() => {
  const root = document.querySelector('[data-pl-signup]');
  if (!root) return;
  const KEY = root.dataset.key;
  const LIST = root.dataset.list;
  const SCROLL = Math.max(0.05, (+root.dataset.scroll || 30) / 100);
  const SNOOZE_DAYS = +root.dataset.snooze || 14;
  const store = {
    get(k) {
      try {
        return localStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set(k, v) {
      try {
        localStorage.setItem(k, v);
      } catch (e) {
        /* private mode: the pop-up may show again next visit */
      }
    },
  };
  const session = {
    get(k) {
      try {
        return sessionStorage.getItem(k);
      } catch (e) {
        return null;
      }
    },
    set(k, v) {
      try {
        sessionStorage.setItem(k, v);
      } catch (e) {
        /* ignore */
      }
    },
  };

  const path = location.pathname;
  /* the editor and QA: ?signup=1 opens it at once, whatever was stored */
  const force = /[?&]signup=1\b/.test(location.search);
  const blocked =
    !KEY ||
    !LIST ||
    (!force &&
      (navigator.webdriver /* automated browsers (tests, bots) never get it */ ||
        /^\/(cart|checkout|account|challenge|password)/.test(path) ||
        document.querySelector('.pl-builder, [data-pl-builder]') ||
        store.get('pl_signup_done') ||
        +store.get('pl_signup_snooze_until') > Date.now() ||
        session.get('pl_signup_shown')));
  const box = root.querySelector('.pl-signup__box');
  const form = root.querySelector('[data-pl-signup-form]');
  const input = root.querySelector('#pl-signup-email');
  const error = root.querySelector('#pl-signup-error');
  const submit = root.querySelector('[data-pl-signup-submit]');
  let lastFocus = null;
  let open = false;
  let detach = () => {};

  const busyElsewhere = () => {
    const html = document.documentElement;
    const banner = document.getElementById('shopify-pc__banner');
    return html.classList.contains('pl-locked') || (banner && banner.style.display !== 'none' && banner.offsetParent !== null);
  };

  const show = () => {
    if (open || blocked || busyElsewhere()) return;
    open = true;
    session.set('pl_signup_shown', '1');
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('pl-signup-open');
    requestAnimationFrame(() => root.classList.add('is-in'));
    setTimeout(() => input.focus({preventScroll: true}), 60);
    detach();
  };
  const hide = (snooze) => {
    if (!open) return;
    open = false;
    if (snooze) store.set('pl_signup_snooze_until', String(Date.now() + SNOOZE_DAYS * 864e5));
    root.classList.remove('is-in');
    document.documentElement.classList.remove('pl-signup-open');
    setTimeout(() => {
      root.hidden = true;
    }, 220);
    if (lastFocus && lastFocus.focus) lastFocus.focus({preventScroll: true});
  };

  root.querySelectorAll('[data-pl-signup-close]').forEach((b) => b.addEventListener('click', () => hide(!store.get('pl_signup_done'))));
  root.querySelector('[data-pl-signup-no]').addEventListener('click', () => hide(true));
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hide(!store.get('pl_signup_done'));
      return;
    }
    if (e.key !== 'Tab') return;
    /* keep focus inside the dialog */
    const f = [...box.querySelectorAll('a[href], button, input')].filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0];
    const last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  const say = (msg) => {
    error.textContent = msg;
    error.hidden = !msg;
    input.setAttribute('aria-invalid', msg ? 'true' : 'false');
  };
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = input.value.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      say('Please enter a valid email address.');
      input.focus();
      return;
    }
    say('');
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(KEY)}`, {
        method: 'POST',
        headers: {'Content-Type': 'application/vnd.api+json', revision: '2026-07-15'},
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'PROLAST site pop-up',
              profile: {data: {type: 'profile', attributes: {email, subscriptions: {email: {marketing: {consent: 'SUBSCRIBED'}}}}}},
            },
            relationships: {list: {data: {type: 'list', id: LIST}}},
          },
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      store.set('pl_signup_done', '1');
      try {
        if (window.klaviyo && window.klaviyo.identify) window.klaviyo.identify({email});
      } catch (err) {
        /* tracking is optional */
      }
      root.querySelector('[data-pl-signup-step="form"]').hidden = true;
      root.querySelector('[data-pl-signup-step="done"]').hidden = false;
      root.querySelector('[data-pl-signup-done]').focus({preventScroll: true});
    } catch (err) {
      say('That didn’t go through. Please try again in a moment.');
    } finally {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }
  });

  /* Klaviyo's own pop-ups (until they are switched off in Klaviyo): the theme hides them (layout/theme.liquid); here
     their own close button is pressed as soon as one opens, so Klaviyo releases its scroll lock and counts it as
     closed. Only dialogs labelled "... Form" inside Klaviyo's wrappers; embedded forms are left alone. */
  if (root.hasAttribute('data-hide-klaviyo')) {
    /* After it closes, Klaviyo hands focus to an element of its own choosing (2026-10-10: on the glove page the
       Cobalt Blue swatch came up framed, as if a second colour were selected, and focus can scroll the page to it).
       Focus and scroll go back to where they were, unless the visitor touched, scrolled or typed in the meantime. */
    let touched = 0;
    const mark = () => { touched = Date.now(); };
    ['pointerdown', 'keydown', 'wheel', 'touchmove'].forEach((t) => addEventListener(t, mark, {passive: true, capture: true}));
    /* The last element focused outside Klaviyo's forms. A form can pull focus onto its own close button before it is
       caught here (2026-10-10: in the open cart drawer), so that is not where focus goes back to. */
    const KL = '[class*="kl-private-reset-css"][role="dialog"]';
    let lastOutside = null;
    addEventListener('focusin', (e) => {
      if (e.target instanceof Element && !e.target.closest(KL)) lastOutside = e.target;
    }, true);
    const closeKlaviyo = () => {
      document.querySelectorAll(`${KL}[aria-label$="Form"]`).forEach((d) => {
        const x = d.querySelector('button.klaviyo-close-form, button[aria-label="Close dialog"]');
        if (x && !d.__plClosed) {
          d.__plClosed = true;
          const now = document.activeElement;
          const prev = now && now !== document.body && !d.contains(now) ? now : lastOutside;
          const y = window.scrollY;
          const at = Date.now();
          const giveBack = (stray) => {
            if (touched > at) return;
            if (prev && prev !== document.body && prev.isConnected && !d.contains(prev)) prev.focus({preventScroll: true});
            else if (stray && document.activeElement === stray) stray.blur();
            if (Math.abs(window.scrollY - y) > 2) window.scrollTo(0, y);
          };
          /* Klaviyo moves focus some time after its close (more than 250 ms on the glove page): catch the move
             itself, for up to 3 s, and undo it; a move into the closed form counts too */
          const onFocus = (e) => {
            if (touched > at || Date.now() - at > 3000) {
              removeEventListener('focusin', onFocus, true);
              return;
            }
            if (e.target === prev) return;
            removeEventListener('focusin', onFocus, true);
            setTimeout(() => giveBack(e.target), 0);
          };
          addEventListener('focusin', onFocus, true);
          setTimeout(() => removeEventListener('focusin', onFocus, true), 3000);
          x.click();
          /* focus left inside the closed form, or dropped to the page: give it back */
          setTimeout(() => {
            const a = document.activeElement;
            if (!a || a === document.body || d.contains(a)) giveBack(null);
          }, 400);
        }
      });
    };
    new MutationObserver(closeKlaviyo).observe(document.body, {childList: true, subtree: true});
    closeKlaviyo();
  }

  if (blocked) return;
  /* triggers: scrolled far enough, or (desktop) the pointer heading for the tabs; never before 8 s */
  const t0 = Date.now();
  const ready = () => Date.now() - t0 > 8000;
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - innerHeight;
    if (max > 0 && scrollY / max >= SCROLL && ready()) show();
  };
  const onLeave = (e) => {
    if (e.clientY <= 0 && !e.relatedTarget && ready()) show();
  };
  detach = () => {
    window.removeEventListener('scroll', onScroll);
    document.documentElement.removeEventListener('mouseleave', onLeave);
  };
  window.addEventListener('scroll', onScroll, {passive: true});
  if (matchMedia('(hover: hover) and (pointer: fine)').matches) document.documentElement.addEventListener('mouseleave', onLeave);
  /* short pages: scrolling can't reach the share, so 25 s on the page will do */
  setTimeout(() => {
    if (document.documentElement.scrollHeight - innerHeight < 400) show();
  }, 25000);
  if (force) setTimeout(show, 300);
})();
